import { useEffect, useState } from 'react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { Bond, BondStatus, mensagemDeErro } from '../api';
import { useAlterarSituacao, useMotivos } from '../hooks/vidaFuncional';
import { BOND_STATUS_LABELS, manualStatusTargets } from './format';
import { PessoasModal } from './PessoasModal';

interface Props {
  bond: Bond | null;
  onClose: () => void;
  onDone: (message: string) => void;
}

/** Alteração manual da situação funcional (US-GP-007), com motivo e justificativa. */
export function StatusChangeModal({ bond, onClose, onDone }: Props) {
  const alterar = useAlterarSituacao();
  const [status, setStatus] = useState('');
  const [reasonId, setReasonId] = useState('');
  const [justification, setJustification] = useState('');
  const [date, setDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const motivos = useMotivos(status, false, !!status);
  const targets = bond ? manualStatusTargets(bond.status) : [];

  useEffect(() => {
    setStatus('');
    setReasonId('');
    setJustification('');
    setDate('');
    setError(null);
  }, [bond]);

  async function submit() {
    if (!bond || !status) return setError('Selecione a nova situação.');
    if (!justification.trim()) return setError('Justificativa é obrigatória.');
    if ((motivos.data ?? []).length > 0 && !reasonId) return setError('Selecione o motivo.');
    try {
      await alterar.mutateAsync({
        bondId: bond.id, status: status as BondStatus, justification: justification.trim(),
        reasonId: reasonId || undefined, date: status === 'falecido' && date ? date : undefined,
      });
      onDone(`Situação alterada para ${BOND_STATUS_LABELS[status as BondStatus]}.`);
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível alterar a situação.'));
    }
  }

  return (
    <PessoasModal
      open={bond !== null}
      title="Alterar situação funcional"
      description={bond ? `Situação atual: ${BOND_STATUS_LABELS[bond.status]}` : undefined}
      submitLabel="Alterar"
      submitVariant={status === 'falecido' ? 'danger' : 'primary'}
      isSubmitting={alterar.isPending}
      submitError={error}
      onClose={onClose}
      onSubmit={submit}
    >
      <AutocompleteSelect
        label="Nova situação"
        options={targets.map((t) => ({ value: t, label: BOND_STATUS_LABELS[t as BondStatus] }))}
        value={status}
        onChange={(v) => { setStatus(v); setReasonId(''); }}
      />
      {(motivos.data ?? []).length > 0 && (
        <AutocompleteSelect label="Motivo" options={(motivos.data ?? []).map((m) => ({ value: m.id, label: m.name }))}
          value={reasonId} onChange={setReasonId} />
      )}
      {status === 'falecido' && (
        <>
          <TextInput label="Data do falecimento" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <p className="text-xs text-red-700">O vínculo será encerrado, o acesso do usuário desativado e as sessões revogadas.</p>
        </>
      )}
      <Textarea label="Justificativa" value={justification} onChange={(e) => setJustification(e.target.value)} />
    </PessoasModal>
  );
}
